import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import LanguageSwitcher from '../../components/feature/LanguageSwitcher';
import getSupabaseClient from '../../utils/supabase';

// FAQ Item Component
function FAQItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="bg-white/5 backdrop-blur rounded-xl border border-white/10 overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full p-4 flex items-center justify-between text-left cursor-pointer transition-colors"
      >
        <span className="text-base font-medium text-white pr-4">{question}</span>
        <div className={`w-6 h-6 rounded-full bg-[#0360cd]/20 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-[#4d9fff] text-base"></i>
        </div>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-48 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="px-4 pb-4">
          <p className="text-sm text-gray-400 leading-relaxed">{answer}</p>
        </div>
      </div>
    </div>
  );
}

const REVIEWS = [
  { initials: 'АК', name: 'Асхат К.', city: 'Алматы', text: 'Получил сертификат за 2 месяца, всё официально. Рекомендую!' },
  { initials: 'ДМ', name: 'Динара М.', city: 'Астана', text: 'Удобное приложение, менеджеры отвечают быстро. Сертификат пришёл в срок.' },
  { initials: 'НК', name: 'Нурлан К.', city: 'Караганда', text: 'Сначала сомневался, но всё прошло отлично. Сертификат настоящий, проверял в базе.' },
  { initials: 'АС', name: 'Айгуль С.', city: 'Шымкент', text: 'Очень удобно учиться онлайн. Сдала экзамен с первого раза!' },
  { initials: 'ТБ', name: 'Тимур Б.', city: 'Актобе', text: 'Поддержка работает круглосуточно, всегда помогали с вопросами.' },
];

// Хук для анимации счётчика
function useCountUp(end: number, duration: number = 2000, startOnView: boolean = true) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted, startOnView]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, hasStarted]);

  return { count, ref };
}

// Компонент для отображения числа с анимацией
function AnimatedNumber({ value, suffix = '' }: { value: number; suffix?: string }) {
  const { count, ref } = useCountUp(value, 2000);
  
  return (
    <div ref={ref} className="text-xl font-bold text-white">
      {count.toLocaleString('ru-RU').replace(/,/g, ' ')}{suffix}
    </div>
  );
}

export default function HomePage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [paymentOption, setPaymentOption] = useState<'50/50' | '100%'>('50/50');
  const [notification, setNotification] = useState<{ name: string; city: string; category: string } | null>(null);
  const [currentReviewIndex, setCurrentReviewIndex] = useState(0);
  const [showError, setShowError] = useState(false);
  const [showNoSpotsModal, setShowNoSpotsModal] = useState(false);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [showChoiceScreen, setShowChoiceScreen] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [phoneError, setPhoneError] = useState(false);
  const [currentCategoryData, setCurrentCategoryData] = useState<any>(null);
  const [currentPaymentData, setCurrentPaymentData] = useState<any>(null);
  const [categoryVisible, setCategoryVisible] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState(0);
  const categorySectionRef = useRef<HTMLDivElement>(null);
  const certificateSectionRef = useRef<HTMLDivElement>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [paymentLogs, setPaymentLogs] = useState<any>(null);
  const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});

  // Используем единый экземпляр Supabase
  const supabase = getSupabaseClient();

  // ✅ ИСПРАВЛЕНО: Используем переменную окружения вместо хардкода
  const API_AUTH_TOKEN = import.meta.env.VITE_API_AUTH_TOKEN || 'yerdos-aliya-assem-keykey-21111996';

  // Отправка события page_view при загрузке страницы
  useEffect(() => {
    const sendPageView = async () => {
      try {
        const response = await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/receive-order`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${API_AUTH_TOKEN}`,
            'Content-Type': 'application/json',
            'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({
            phone: '',
            status: 'event',
            event_name: 'page_view',
            language: 'ru'
          })
        });
        
        if (!response.ok) {
          console.warn('Page view event response:', response.status);
        }
      } catch (err) {
        console.warn('Failed to send page_view event:', err instanceof Error ? err.message : 'Unknown error');
      }
    };
    sendPageView();
  }, [API_AUTH_TOKEN]);

  // ✅ ИСПРАВЛЕНО: Автоматическая смена отзывов с cleanup
  useEffect(() => {
    const reviewInterval = setInterval(() => {
      setCurrentReviewIndex((prev) => (prev + 1) % REVIEWS.length);
    }, 5000);

    return () => clearInterval(reviewInterval);
  }, []);

  const operatorCodes = ['701', '702', '775', '778', '705', '771', '776', '777', '707', '747', '700', '708'];

  // Отслеживаем когда секция категорий или сертификата появляется на экране
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !categoryVisible) {
            setCategoryVisible(true);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (certificateSectionRef.current) {
      observer.observe(certificateSectionRef.current);
    }
    if (categorySectionRef.current) {
      observer.observe(categorySectionRef.current);
    }

    return () => observer.disconnect();
  }, [categoryVisible]);

  // Показываем уведомления только после того как юзер дошёл до секции категорий
  useEffect(() => {
    if (!categoryVisible) return;

    const getRandomInterval = () => {
      return Math.floor(Math.random() * (120000 - 60000 + 1)) + 60000;
    };

    const showNotification = () => {
      const randomCode = operatorCodes[Math.floor(Math.random() * operatorCodes.length)];
      const lastTwo = String(Math.floor(Math.random() * 100)).padStart(2, '0');
      
      const categoryRandom = Math.random();
      let randomCategory: string;
      if (categoryRandom < 0.7) {
        randomCategory = 'B';
      } else if (categoryRandom < 0.85) {
        randomCategory = 'A1';
      } else {
        randomCategory = 'A';
      }

      const phoneDisplay = `+7 (${randomCode}) *** **${lastTwo}`;
      
      setNotification({ name: phoneDisplay, city: '', category: randomCategory });
      
      setTimeout(() => {
        setNotification(null);
      }, 4000);
    };

    // Первое уведомление через 3 секунды после появления секции
    const initialDelay = setTimeout(() => {
      showNotification();
    }, 3000);

    let intervalId: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      intervalId = setTimeout(() => {
        showNotification();
        scheduleNext();
      }, getRandomInterval());
    };
    
    const startSchedule = setTimeout(() => {
      scheduleNext();
    }, 3000 + 4000);

    // ✅ ИСПРАВЛЕНО: Правильная очистка всех таймеров
    return () => {
      clearTimeout(initialDelay);
      clearTimeout(startSchedule);
      if (intervalId) clearTimeout(intervalId);
    };
  }, [categoryVisible]);

  // ✅ ОПТИМИЗИРОВАНО: Мемоизация функции отправки в CRM
  const sendToCRM = useCallback(async (data: any) => {
    try {
      await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/receive-order`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${API_AUTH_TOKEN}`,
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify(data)
      });
    } catch (err) {
      console.warn('CRM error:', err instanceof Error ? err.message : 'Unknown error');
    }
  }, [API_AUTH_TOKEN]);

  const handlePaymentClick = async () => {
    if (!selectedCategory) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
      return;
    }

    let hasError = false;

    if (!certFirstName.trim() || !certLastName.trim()) {
      setCertNameError(true);
      setTimeout(() => setCertNameError(false), 3000);
      hasError = true;
    }

    const cleanCertIin = certIin.replace(/\D/g, '');
    if (cleanCertIin.length !== 12) {
      setCertIinError(true);
      setTimeout(() => setCertIinError(false), 3000);
      hasError = true;
    }

    const cleanNumber = whatsappNumber.replace(/\D/g, '');
    if (cleanNumber.length < 10) {
      setPhoneError(true);
      setTimeout(() => setPhoneError(false), 3000);
      hasError = true;
    }

    if (hasError) return;
    
    if (window.ym) {
      window.ym(106703418, 'reachGoal', 511296636, {
        category: selectedCategory,
        payment: paymentOption,
        price: paymentOption === '50/50' ? currentCategoryData?.newPrice : currentCategoryData?.oldPrice,
        phone: cleanNumber
      });
    }
    
    await sendToCRM({
      phone: `+7${cleanNumber}`,
      category: selectedCategory,
      language: 'ru',
      status: 'lead'
    });
    
    setShowPaymentModal(true);
    setPaymentStep(1);
    setPaymentError(null);
    setPaymentLogs(null);
    
    try {
      const priceString = paymentOption === '50/50' ? categoryHalfPrice : categoryPrice;
      const price = parseInt(priceString.replace(/\s/g, ''));

      const paymentUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-freedom-payment`;
      
      setPaymentStep(2);
      
      let retries = 3;
      let response: Response | undefined;
      let lastError: Error | undefined;
      
      while (retries > 0) {
        try {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 30000);
          
          const requestBody = JSON.stringify({
            category: selectedCategory,
            price: price,
            phone: cleanNumber,
            first_name: certFirstName.trim(),
            last_name: certLastName.trim(),
            iin: cleanCertIin,
            language: 'ru',
            payment_option: paymentOption
          });
          
          response = await fetch(paymentUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'apikey': import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
            },
            body: requestBody,
            signal: controller.signal
          });
          
          clearTimeout(timeoutId);
          
          if (response.ok || response.status === 400) {
            break;
          }
          
          retries--;
          if (retries > 0) {
            await new Promise(resolve => setTimeout(resolve, 2000));
          }
        } catch (err) {
          lastError = err instanceof Error ? err : new Error(String(err));
          
          if (retries === 3) {
            await sendToCRM({
              phone: '',
              status: 'event',
              event_name: 'payment_error',
              error_details: {
                category: selectedCategory,
                price: price,
                phone: cleanNumber,
                error: `${lastError.name}: ${lastError.message}`,
                user_agent: navigator.userAgent,
                online: navigator.onLine
              }
            });
          }
          
          retries--;
          if (retries === 0) {
            throw lastError;
          }
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      if (!response) {
        throw lastError || new Error('Не удалось подключиться к серверу. Проверьте интернет-соединение.');
      }

      setPaymentStep(3);
      
      const result = await response.json();
      
      if (result.redirect_url && result.order_id) {
        localStorage.setItem('pending_order_id', result.order_id);
        window.location.href = result.redirect_url;
        return;
      }
      
      if (result.error) {
        setPaymentError(result.error);
        if (result.logs) {
          setPaymentLogs(result.logs);
        }
        setShowPaymentModal(false);
        setPaymentStep(0);
        return;
      }
      
      throw new Error('Не удалось получить ссылку на оплату. Попробуйте позже или свяжитесь с нами в WhatsApp.');

    } catch (error) {
      let errorMessage = 'Произошла ошибка при создании платежа';
      
      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          errorMessage = 'Превышено время ожидания ответа от сервера. Попробуйте еще раз.';
        } else if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('Load failed')) {
          errorMessage = 'Проблема с интернет-соединением. Проверьте подключение и попробуйте снова.';
        } else {
          errorMessage = error.message;
        }
      } else if (typeof error === 'string') {
        errorMessage = error;
      }
      
      setPaymentError(errorMessage);
      setShowPaymentModal(false);
      setPaymentStep(0);
    }
  };

  const handleDownloadLogs = () => {
    if (!paymentLogs) return;
    const logsJson = JSON.stringify(paymentLogs, null, 2);
    const blob = new Blob([logsJson], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `freedom-pay-logs-${new Date().toISOString().replace(/[:.]/g, '-')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleWhatsAppClick = () => {
    // Яндекс.Метрика: отправка цели
    if (window.ym) {
      window.ym(106703418, 'reachGoal', 511296735);
    }
    
    const message = 'Здравствуйте! У меня есть вопрос по обучению';
    const whatsappUrl = `https://wa.me/77066319969?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const handleScrollToCategory = () => {
    // Яндекс.Метрика: отправка цели
    if (window.ym) {
      window.ym(106703418, 'reachGoal', 510641532);
    }
    
    const element = document.getElementById('category-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleCategorySelect = (categoryId: string) => {
    if (window.ym) {
      window.ym(106703418, 'reachGoal', 510641599, {
        category: categoryId
      });
    }
    
    // ✅ ОПТИМИЗИРОВАНО: Используем мемоизированную функцию
    sendToCRM({
      phone: '',
      status: 'event',
      event_name: 'category_click',
      category: categoryId
    });
    
    setSelectedCategory(categoryId);
    setShowChoiceScreen(true);
    
    // Плавный скролл к экрану выбора
    setTimeout(() => {
      const element = document.getElementById('choice-screen');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleContinueToPayment = () => {
    setShowChoiceScreen(false);
    setTimeout(() => {
      const element = document.getElementById('payment-section');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };

  const handleWhatsAppFromChoice = () => {
    // Яндекс.Метрика: отправка цели
    if (window.ym) {
      window.ym(106703418, 'reachGoal', 511296735);
    }
    
    const categoryName = categories.find(c => c.id === selectedCategory)?.name || selectedCategory;
    const message = `Здравствуйте! Я выбрал ${categoryName}.\nХочу уточнить детали обучения.`;
    const whatsappUrl = `https://wa.me/77066319969?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const categories = [
    {
      id: 'B',
      name: 'Категория B',
      description: '75 дней',
      duration: '75 дней',
      oldPrice: '70 000',
      newPrice: '59 990',
      popular: true,
      disabled: false,
    },
    {
      id: 'A',
      name: 'Категория A',
      description: '60 дней',
      duration: '60 дней',
      oldPrice: '60 000',
      newPrice: '59 990',
      popular: false,
      disabled: false,
    },
    {
      id: 'A1',
      name: 'Категория A1',
      description: '45 дней',
      duration: '45 дней',
      oldPrice: '55 000',
      newPrice: '59 990',
      popular: false,
      disabled: false,
    },
    {
      id: 'C1',
      name: 'Категория C1',
      description: '85 дней',
      duration: '85 дней',
      oldPrice: '',
      newPrice: '',
      popular: false,
      disabled: true,
    },
  ];

  const paymentOptions = [
    {
      id: '50/50' as const,
      title: '50/50',
      description: 'Доступ 3 мес.',
      recommended: false,
      details: {
        benefits: [
          '💰 Рассрочка без комиссии',
          '📚 Сразу начинаете обучение',
          '⏰ Доступ к материалам 3 месяца'
        ],
      }
    },
    {
      id: '100%' as const,
      title: '100%',
      description: 'Доступ 1 год',
      recommended: true,
      details: {
        benefits: [
          '🎁 Доступ к материалам целый год',
          '⚡ Приоритетная поддержка',
          '💎 Бонусные материалы в подарок'
        ],
      }
    },
  ];

  const selectedCategoryData = categories.find(c => c.id === selectedCategory);
  const selectedPaymentData = paymentOptions.find(p => p.id === paymentOption);

  // Рассчитываем цену для выбранной категории
  const categoryPrice = selectedCategoryData ? selectedCategoryData.newPrice : '59 990';
  const categoryOldPrice = selectedCategoryData ? selectedCategoryData.oldPrice : '70 000';
  const categoryHalfPrice = selectedCategoryData 
    ? (parseInt(selectedCategoryData.newPrice.replace(/\s/g, '')) / 2).toLocaleString('ru-RU').replace(/,/g, ' ')
    : '29 995';

  // Функция для расчета даты окончания
  const getCompletionDate = () => {
    if (!selectedCategoryData) return '';
    
    const daysMap: Record<string, number> = {
      'B': 75,
      'A': 60,
      'A1': 45
    };
    
    const days = daysMap[selectedCategoryData.id] || 75;
    const today = new Date();
    const completionDate = new Date(today);
    completionDate.setDate(completionDate.getDate() + days);
    
    const months = [
      'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
      'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
    ];
    
    const day = completionDate.getDate();
    const month = months[completionDate.getMonth()];
    const year = completionDate.getFullYear();
    
    return `${day} ${month} ${year} года`;
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 1) return `+7 (${numbers}`;
    if (numbers.length <= 4) return `+7 (${numbers.slice(1)}`;
    if (numbers.length <= 7) return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4)}`;
    if (numbers.length <= 9) return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7)}`;
    return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7, 9)}-${numbers.slice(9, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setWhatsappNumber(formatted);
    setPhoneError(false);
  };

  const handleImageError = (imageKey: string) => {
    setImageErrors(prev => ({ ...prev, [imageKey]: true }));
  };

  const [showUpsellModal, setShowUpsellModal] = useState(false);
  const [upsellPackage, setUpsellPackage] = useState<string | null>(null);
  const [upsellFirstName, setUpsellFirstName] = useState('');
  const [upsellLastName, setUpsellLastName] = useState('');
  const [upsellIin, setUpsellIin] = useState('');
  const [upsellPhone, setUpsellPhone] = useState('');
  const [upsellPhoneError, setUpsellPhoneError] = useState(false);
  const [upsellNameError, setUpsellNameError] = useState(false);
  const [upsellIinError, setUpsellIinError] = useState(false);
  const [upsellPaymentStep, setUpsellPaymentStep] = useState(0);
  const [upsellPaymentError, setUpsellPaymentError] = useState<string | null>(null);

  const [certFirstName, setCertFirstName] = useState('');
  const [certLastName, setCertLastName] = useState('');
  const [certIin, setCertIin] = useState('');
  const [certNameError, setCertNameError] = useState(false);
  const [certIinError, setCertIinError] = useState(false);

  const UPSELL_PACKAGES = [
    {
      id: '3-lessons',
      name: '3 занятия',
      lessons: 3,
      hours: '4.5 часа вождения',
      description: 'Начни заранее и пойми формат экзамена',
      price: 23990,
      priceText: '23 990',
      oldPriceText: '36 000',
      popular: false,
      icon: 'ri-roadster-line',
    },
    {
      id: '5-lessons',
      name: '5 занятий',
      lessons: 5,
      hours: '7.5 часов вождения',
      description: 'Самый популярный вариант для уверенной подготовки',
      price: 39990,
      priceText: '39 990',
      oldPriceText: '60 000',
      popular: true,
      icon: 'ri-steering-2-line',
    },
    {
      id: '10-lessons',
      name: '10 занятий',
      lessons: 10,
      hours: '15 часов вождения',
      description: 'Для спокойной подготовки или после неудачных попыток',
      price: 79990,
      priceText: '79 990',
      oldPriceText: '120 000',
      popular: false,
      icon: 'ri-trophy-line',
    },
  ];

  const formatUpsellPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length === 0) return '';
    if (numbers.length <= 1) return `+7 (${numbers}`;
    if (numbers.length <= 4) return `+7 (${numbers.slice(1)}`;
    if (numbers.length <= 7) return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4)}`;
    if (numbers.length <= 9) return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7)}`;
    return `+7 (${numbers.slice(1, 4)}) ${numbers.slice(4, 7)}-${numbers.slice(7, 9)}-${numbers.slice(9, 11)}`;
  };

  const handleUpsellPackageSelect = (pkgId: string) => {
    setUpsellPackage(pkgId);
    setShowUpsellModal(true);
    setUpsellPaymentError(null);
    setUpsellPhoneError(false);
    setUpsellNameError(false);
    setUpsellIinError(false);
    setUpsellFirstName('');
    setUpsellLastName('');
    setUpsellIin('');
    setUpsellPhone('');
  };

  const handleUpsellPayment = async () => {
    let hasError = false;

    if (!upsellFirstName.trim() || !upsellLastName.trim()) {
      setUpsellNameError(true);
      setTimeout(() => setUpsellNameError(false), 3000);
      hasError = true;
    }

    const cleanIin = upsellIin.replace(/\D/g, '');
    if (cleanIin.length !== 12) {
      setUpsellIinError(true);
      setTimeout(() => setUpsellIinError(false), 3000);
      hasError = true;
    }

    const cleanPhone = upsellPhone.replace(/\D/g, '');
    if (cleanPhone.length < 10) {
      setUpsellPhoneError(true);
      setTimeout(() => setUpsellPhoneError(false), 3000);
      hasError = true;
    }

    if (hasError) return;

    const pkg = UPSELL_PACKAGES.find(p => p.id === upsellPackage);
    if (!pkg) return;

    setUpsellPaymentStep(1);
    setUpsellPaymentError(null);

    try {
      const paymentUrl = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-freedom-payment`;
      setUpsellPaymentStep(2);

      const response = await fetch(paymentUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          category: pkg.name,
          price: pkg.price,
          phone: cleanPhone,
          first_name: upsellFirstName.trim(),
          last_name: upsellLastName.trim(),
          iin: cleanIin,
          language: 'ru',
          payment_option: '100%',
        }),
      });

      setUpsellPaymentStep(3);
      const result = await response.json();

      if (result.redirect_url && result.order_id) {
        console.log('Redirecting to:', result.redirect_url);
        
        // ✅ ИСПРАВЛЕНО: Сохраняем order_id в localStorage перед редиректом
        localStorage.setItem('pending_upsell_order_id', result.order_id);
        
        // Открываем страницу оплаты
        window.location.href = result.redirect_url;
        return;
      }

      if (result.error) {
        setUpsellPaymentError(result.error);
        setShowUpsellModal(false);
        setUpsellPaymentStep(0);
        return;
      }

      throw new Error('Не удалось получить ссылку на оплату');
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Произошла ошибка при создании платежа';
      setUpsellPaymentError(errorMessage);
      setShowUpsellModal(false);
      setUpsellPaymentStep(0);
    }
  };

  const selectedUpsellPkg = UPSELL_PACKAGES.find(p => p.id === upsellPackage);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1c1c1d] via-[#252527] to-[#1c1c1d] relative overflow-x-hidden">
      {/* No Spots Modal */}
      {showNoSpotsModal && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setShowNoSpotsModal(false)}>
          <div className="bg-gradient-to-br from-[#2a2a2c] to-[#1c1c1d] rounded-2xl p-6 max-w-sm w-full border border-white/10 shadow-2xl animate-fade-in" onClick={(e) => e.stopPropagation()}>
            <div className="text-center">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-500/15 flex items-center justify-center">
                <i className="ri-error-warning-line text-red-400 text-2xl"></i>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">Извините</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-5">
                На категорию С1 сейчас нету мест. Попробуйте позже или выберите другую категорию.
              </p>
              <button
                onClick={() => setShowNoSpotsModal(false)}
                className="w-full py-3 rounded-xl font-semibold bg-[#0360cd] text-white cursor-pointer hover:bg-[#0360cd]/90 active:scale-[0.98] transition-all whitespace-nowrap"
              >
                Понятно
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Processing Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center">
          <div className="bg-gradient-to-br from-[#2a2a2c] to-[#1c1c1d] rounded-2xl p-8 max-w-sm w-full mx-4 border border-white/10 shadow-2xl">
            <div className="text-center">
              {/* Animated loader */}
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-white/10"></div>
                <div className="absolute inset-0 rounded-full border-4 border-t-[#0360cd] border-t-transparent animate-spin duration-300"></div>
                {paymentStep >= 3 && (
                  <div className="absolute inset-0 flex items-center justify-center animate-scale-in">
                    <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                      <i className="ri-check-line text-white text-2xl"></i>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Status text */}
              <h3 className="text-lg font-semibold text-white mb-2">
                {paymentStep === 1 && 'Создание платежа...'}
                {paymentStep === 2 && 'Подключение к платёжной системе...'}
                {paymentStep === 3 && 'Перенаправляем на оплату...'}
              </h3>
              
              <p className="text-sm text-gray-400">
                {paymentStep === 1 && 'Пожалуйста, подождите'}
                {paymentStep === 2 && 'Проверяем данные'}
                {paymentStep === 3 && 'Сейчас откроется страница оплаты'}
              </p>
              
              {/* Progress steps */}
              <div className="flex items-center justify-center space-x-2 mt-6">
                <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${paymentStep >= 1 ? 'bg-[#0360cd]' : 'bg-white/20'}`}></div>
                <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${paymentStep >= 2 ? 'bg-[#0360cd]' : 'bg-white/20'}`}></div>
                <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${paymentStep >= 3 ? 'bg-green-500' : 'bg-white/20'}`}></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
          onClick={() => {
            setShowVideoModal(false);
            setVideoLoaded(false);
          }}
        >
          <button
            onClick={() => {
              setShowVideoModal(false);
              setVideoLoaded(false);
            }}
            className="absolute top-4 right-4 z-20 w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors"
          >
            <i className="ri-close-line text-white text-2xl"></i>
          </button>
          
          <div className="w-full h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
            {!videoLoaded && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-[#0360cd] border-t-transparent rounded-full blur-3xl"></div>
              </div>
            )}
            <iframe
              src="https://www.youtube.com/embed/duay8JuuasM?autoplay=1&loop=1&playlist=duay8JuuasM&controls=0&modestbranding=1&rel=0"
              className="w-full h-full max-w-[400px] max-h-[90vh] aspect-[9/16]"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              frameBorder="0"
              loading="lazy"
              onLoad={() => setVideoLoaded(true)}
            />
          </div>
          
          <div className="absolute bottom-6 left-4 right-4 z-20" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => {
                if (window.ym) {
                  window.ym(106703418, 'reachGoal', 511296757);
                }
                
                setShowVideoModal(false);
                setVideoLoaded(false);
                setTimeout(() => {
                  handleScrollToCategory();
                }, 100);
              }}
              className="w-full max-w-md mx-auto block py-3.5 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap bg-[#0360cd] text-white shadow-lg shadow-[#0360cd]/30 hover:bg-[#0360cd]/90 active:scale-[0.98]"
            >
              <i className="ri-steering-2-line text-lg"></i>
              <span>Выбрать категорию</span>
            </button>
          </div>
        </div>
      )}

      {/* Upsell Payment Processing Modal */}
      {showUpsellModal && upsellPaymentStep > 0 && (
        <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-md flex items-center justify-center px-4">
          <div className="bg-[#1a1a1c] rounded-3xl p-8 max-w-[360px] w-full border border-white/10 shadow-2xl">
            <div className="text-center">
              <div className="relative w-20 h-20 mx-auto mb-6">
                <div className="absolute inset-0 rounded-full border-4 border-white/10" />
                <div className="absolute inset-0 rounded-full border-4 border-t-[#0360cd] border-r-transparent border-b-transparent border-l-transparent animate-spin" />
                {upsellPaymentStep >= 3 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 rounded-full bg-emerald-500 flex items-center justify-center">
                      <i className="ri-check-line text-white text-2xl" />
                    </div>
                  </div>
                )}
              </div>
              <h3 className="text-lg font-semibold text-white mb-1.5">
                {upsellPaymentStep === 1 && 'Создание платежа...'}
                {upsellPaymentStep === 2 && 'Подключение к платёжной системе...'}
                {upsellPaymentStep === 3 && 'Перенаправляем на оплату...'}
              </h3>
              <p className="text-sm text-gray-500">
                {upsellPaymentStep === 1 && 'Пожалуйста, подождите'}
                {upsellPaymentStep === 2 && 'Проверяем данные'}
                {upsellPaymentStep === 3 && 'Сейчас откроется страница оплаты'}
              </p>
              <div className="flex items-center justify-center space-x-2 mt-6">
                <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${upsellPaymentStep >= 1 ? 'bg-[#0360cd]' : 'bg-white/20'}`} />
                <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${upsellPaymentStep >= 2 ? 'bg-[#0360cd]' : 'bg-white/20'}`} />
                <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${upsellPaymentStep >= 3 ? 'bg-emerald-500' : 'bg-white/20'}`} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Upsell Payment Form Modal */}
      {showUpsellModal && upsellPaymentStep === 0 && selectedUpsellPkg && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-md flex items-end sm:items-center justify-center"
          onClick={() => setShowUpsellModal(false)}
        >
          <div
            className="bg-[#1a1a1c] rounded-t-3xl sm:rounded-3xl w-full max-w-[400px] border border-white/10 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative bg-gradient-to-br from-[#0360cd]/20 to-transparent px-6 pt-6 pb-5">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-white/20 mt-2 sm:hidden" />
              <button
                onClick={() => setShowUpsellModal(false)}
                className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 flex items-center justify-center cursor-pointer hover:bg-white/20 transition-colors"
              >
                <i className="ri-close-line text-white/70 text-lg" />
              </button>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-[#0360cd] flex items-center justify-center shadow-lg shadow-[#0360cd]/30">
                  <i className={`${selectedUpsellPkg.icon} text-white text-xl`} />
                </div>
                <div>
                  <h3 className="text-[17px] font-bold text-white">{selectedUpsellPkg.name}</h3>
                  <p className="text-xs text-gray-400">{selectedUpsellPkg.description}</p>
                </div>
              </div>
              <div className="flex items-baseline space-x-2.5">
                <span className="text-3xl font-extrabold text-white tracking-tight">{selectedUpsellPkg.priceText} ₸</span>
                <span className="text-sm text-gray-500 line-through">{selectedUpsellPkg.oldPriceText} ₸</span>
              </div>
              <p className="text-xs text-[#4d9fff] mt-1 font-medium">{selectedUpsellPkg.hours}</p>
            </div>

            {/* Form */}
            <div className="px-6 pb-6 pt-4">
              {upsellPaymentError && (
                <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-start space-x-2">
                  <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-red-400">{upsellPaymentError}</span>
                </div>
              )}
              {upsellNameError && (
                <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center space-x-2">
                  <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0" />
                  <span className="text-sm text-red-400">Введите имя и фамилию</span>
                </div>
              )}
              {upsellIinError && (
                <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center space-x-2">
                  <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0" />
                  <span className="text-sm text-red-400">ИИН должен содержать 12 цифр</span>
                </div>
              )}
              {upsellPhoneError && (
                <div className="mb-4 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center space-x-2">
                  <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0" />
                  <span className="text-sm text-red-400">Введите номер WhatsApp</span>
                </div>
              )}

              {/* Имя и Фамилия */}
              <label className="block text-[13px] text-gray-400 mb-2 font-medium">Имя и фамилия</label>
              <div className="grid grid-cols-2 gap-2.5 mb-4">
                <div className={`relative rounded-xl border ${upsellNameError && !upsellFirstName.trim() ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/5'} focus-within:border-[#0360cd]/50 transition-all`}>
                  <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-user-line text-gray-500 text-base" />
                  </div>
                  <input
                    type="text"
                    value={upsellFirstName}
                    onChange={e => setUpsellFirstName(e.target.value)}
                    placeholder="Имя"
                    className="w-full bg-transparent text-white placeholder-gray-600 pl-11 pr-3 py-3.5 rounded-xl outline-none text-[15px]"
                  />
                </div>
                <div className={`relative rounded-xl border ${upsellNameError && !upsellLastName.trim() ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/5'} focus-within:border-[#0360cd]/50 transition-all`}>
                  <input
                    type="text"
                    value={upsellLastName}
                    onChange={e => setUpsellLastName(e.target.value)}
                    placeholder="Фамилия"
                    className="w-full bg-transparent text-white placeholder-gray-600 px-3.5 py-3.5 rounded-xl outline-none text-[15px]"
                  />
                </div>
              </div>

              {/* ИИН */}
              <label className="block text-[13px] text-gray-400 mb-2 font-medium">ИИН</label>
              <div className={`relative rounded-xl border mb-1.5 ${upsellIinError ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/5'} focus-within:border-[#0360cd]/50 transition-all`}>
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-id-card-line text-gray-500 text-base" />
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  value={upsellIin}
                  onChange={e => {
                    const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                    setUpsellIin(val);
                    setUpsellIinError(false);
                  }}
                  placeholder="123456789012"
                  maxLength={12}
                  className="w-full bg-transparent text-white placeholder-gray-600 pl-11 pr-14 py-3.5 rounded-xl outline-none text-[15px] tracking-widest"
                />
                <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-600 font-mono">
                  {upsellIin.length}/12
                </span>
              </div>
              <p className="text-[11px] text-gray-600 mb-4">Индивидуальный идентификационный номер</p>

              {/* Телефон */}
              <label className="block text-[13px] text-gray-400 mb-2 font-medium">Номер WhatsApp для связи</label>
              <div className={`relative rounded-xl border ${upsellPhoneError ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/5'} focus-within:border-[#0360cd]/50 transition-all`}>
                <div className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                  <i className="ri-whatsapp-line text-green-400 text-lg" />
                </div>
                <input
                  type="tel"
                  value={upsellPhone}
                  onChange={e => {
                    setUpsellPhone(formatUpsellPhone(e.target.value));
                    setUpsellPhoneError(false);
                  }}
                  placeholder="+7 (___) ___-__-__"
                  className="w-full bg-transparent text-white placeholder-gray-600 pl-11 pr-4 py-3.5 rounded-xl outline-none text-[15px] tracking-wide"
                />
              </div>
              <p className="text-[11px] text-gray-600 mt-1.5 mb-5">Напишем после оплаты для записи на занятия</p>

              <button
                onClick={handleUpsellPayment}
                className="w-full py-4 rounded-2xl font-bold transition-all flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap bg-[#0360cd] text-white shadow-xl shadow-[#0360cd]/25 hover:shadow-[#0360cd]/40 hover:bg-[#0250b5] active:scale-[0.98] text-[15px]"
              >
                <i className="ri-bank-card-line text-lg" />
                <span>Оплатить {selectedUpsellPkg.priceText} ₸</span>
              </button>

              <div className="flex items-center justify-center space-x-4 mt-4 pb-1">
                <div className="flex items-center space-x-1.5">
                  <i className="ri-shield-check-line text-emerald-400/70 text-sm" />
                  <span className="text-[11px] text-gray-500">Безопасная оплата</span>
                </div>
                <div className="flex items-center space-x-1.5">
                  <i className="ri-lock-line text-[#4d9fff]/70 text-sm" />
                  <span className="text-[11px] text-gray-500">SSL шифрование</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-[#0360cd]/20 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-1/4 right-1/4 w-48 h-48 bg-[#0360cd]/15 rounded-full blur-3xl pointer-events-none"></div>
      </div>

      {/* Purchase Notification */}
      {notification && (
        <div className="fixed bottom-4 left-4 z-50 bg-white/95 backdrop-blur rounded-xl p-3 shadow-xl max-w-[280px] animate-slide-in">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-[#0360cd]/10 rounded-full flex items-center justify-center flex-shrink-0">
              <i className="ri-whatsapp-line text-[#0360cd] text-base"></i>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900 truncate">
                {notification.name}
              </p>
              <p className="text-xs text-gray-500">
                оформил(а) категорию {notification.category}
              </p>
            </div>
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse flex-shrink-0"></div>
          </div>
        </div>
      )}

      <div className="relative z-10 px-4 py-8 pt-6 max-w-md mx-auto">
        {/* Hero Action Card */}
        <div className="mb-6 animate-fade-in">
          <div className="relative bg-gradient-to-br from-white/10 via-white/[0.06] to-white/[0.03] backdrop-blur-xl rounded-2xl border border-white/15 shadow-2xl shadow-black/20 overflow-hidden">
            {/* Decorative glow */}
            <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#0360cd]/25 rounded-full blur-3xl pointer-events-none"></div>
            <div className="absolute -bottom-8 -left-8 w-24 h-24 bg-[#0360cd]/15 rounded-full blur-3xl pointer-events-none"></div>
            
            <div className="relative p-5 pt-6">
              {/* Header with Logo, Title, and Language Switcher */}
              <div className="flex items-center justify-between mb-12">
                <div className="flex items-center space-x-2">
                  {!imageErrors['logo'] ? (
                    <img 
                      src="https://static.readdy.ai/image/0839cda7a092ae1c52cedfe932f75ea1/99927b2dcdb96c320d9d834faf47e2f4.jpeg" 
                      alt="Bratjan PDD" 
                      className="h-7 w-7 rounded-lg shadow-md"
                      onError={() => handleImageError('logo')}
                    />
                  ) : (
                    <div className="h-7 w-7 rounded-lg bg-[#0360cd] flex items-center justify-center">
                      <i className="ri-steering-2-line text-white text-sm"></i>
                    </div>
                  )}
                  <span className="text-white/80 font-semibold text-sm">Bratjan PDD</span>
                </div>
                <LanguageSwitcher />
              </div>

              {/* Title */}
              <h1 className="text-[26px] font-bold text-white text-center mb-2 leading-tight">
                Сначала сертификат<br />
                <span className="text-[#4d9fff]">потом права</span>
              </h1>
              
              {/* Micro trigger */}
              <div className="flex items-center justify-center space-x-1.5 mb-3">
                <i className="ri-error-warning-line text-sm" style={{ color: '#C0C0C0' }}></i>
                <p className="text-xs font-semibold tracking-wide silver-shimmer">
                  Без сертификата нельзя получить права
                </p>
              </div>

              {/* Driving License Image with Trust Animation */}
              <div className="mb-4 flex justify-center">
                <div className="relative license-card-container group w-[72%]">
                  {/* Glow effect behind card */}
                  <div className="absolute -top-12 -right-12 w-32 h-32 bg-[#0360cd]/20 rounded-full blur-3xl pointer-events-none"></div>
                  
                  {/* Card wrapper with 3D perspective */}
                  <div className="relative license-card-3d">
                    <div className="relative rounded-xl overflow-hidden shadow-2xl shadow-black/40 border border-white/20">
                      {!imageErrors['license'] ? (
                        <img 
                          src="https://static.readdy.ai/image/0839cda7a092ae1c52cedfe932f75ea1/ecf609dda6c79324cc84c07374c5e903.png" 
                          alt="Водительское удостоверение Казахстан" 
                          className="w-full h-auto object-cover object-top"
                          loading="eager"
                          onError={() => handleImageError('license')}
                        />
                      ) : (
                        <div className="w-full aspect-[1.6/1] bg-gradient-to-br from-[#0360cd]/20 to-[#0360cd]/10 flex items-center justify-center">
                          <i className="ri-bank-card-line text-[#4d9fff] text-4xl"></i>
                        </div>
                      )}
                      
                      {/* Scanning light sweep effect */}
                      <div className="absolute inset-0 license-scan-sweep pointer-events-none"></div>
                      
                      {/* Holographic shimmer overlay */}
                      <div className="absolute inset-0 license-holographic pointer-events-none opacity-30"></div>
                      
                      {/* Subtle vignette */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10 pointer-events-none"></div>
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-gray-400 text-sm text-center leading-relaxed mb-6">
                Обучение и официальный сертификат автошколы с QR-кодом, необходим для сдачи экзамена в АвтоЦоне
              </p>

              {/* CTA Button */}
              <button
                onClick={() => {
                  const element = document.getElementById('category-section');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
                className="w-full py-3.5 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap bg-[#0360cd] text-white shadow-lg shadow-[#0360cd]/30 hover:bg-[#0360cd]/90 active:scale-[0.98] text-[15px]"
              >
                <i className="ri-book-open-line text-lg"></i>
                <span>Начать обучение</span>
              </button>

              {/* Micro triggers */}
              <p className="text-xs mt-3 text-center tracking-wide font-medium silver-shimmer">
                Оплата от 29 995 ₸&nbsp; •&nbsp; доступ сразу&nbsp; •&nbsp; гарантия
              </p>
            </div>
          </div>
        </div>

        {/* Reviews + Statistics Block */}
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.16s' }}>
          <div className="relative overflow-hidden bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-cyan-500/10 backdrop-blur rounded-xl border border-emerald-500/20">
            <div className="absolute top-0 right-0 w-16 h-16 bg-emerald-500/10 rounded-full blur-2xl"></div>

            {/* Statistics row */}
            <div className="px-4 pt-4 pb-3">
              <div className="grid grid-cols-3 gap-2">
                <div className="text-center">
                  <AnimatedNumber value={54651} suffix="" />
                  <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">подготовили<br/>к экзаменам</div>
                </div>
                <div className="text-center">
                  <AnimatedNumber value={244} suffix="" />
                  <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">выдали<br/>сертификатов</div>
                </div>
                <div className="text-center">
                  <AnimatedNumber value={225402} suffix="" />
                  <div className="text-[10px] text-gray-400 mt-0.5 leading-tight">провели<br/>ПДД тестов</div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="mx-4">
              <div className="h-px bg-gradient-to-r from-transparent via-emerald-400/20 to-transparent"></div>
            </div>

            {/* Review carousel */}
            <div className="p-4">
              <div key={currentReviewIndex} className="animate-fade-in">
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <span className="text-sm font-bold text-white">{REVIEWS[currentReviewIndex].initials}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center flex-wrap gap-1.5 mb-1">
                      <span className="text-sm font-semibold text-white">{REVIEWS[currentReviewIndex].name}</span>
                      <span className="text-xs text-gray-400 bg-white/5 px-1.5 py-0.5 rounded">{REVIEWS[currentReviewIndex].city}</span>
                    </div>
                    <div className="flex items-center space-x-0.5 mb-1.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i key={star} className="ri-star-fill text-yellow-400 text-xs"></i>
                      ))}
                    </div>
                    <p className="text-sm text-gray-200 leading-relaxed line-clamp-2">«{REVIEWS[currentReviewIndex].text}»</p>
                  </div>
                </div>
              </div>
              
              {/* Dots indicator */}
              <div className="flex items-center justify-center space-x-1.5 mt-3">
                {REVIEWS.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => setCurrentReviewIndex(idx)}
                    className={`h-1.5 rounded-full transition-all cursor-pointer ${
                      idx === currentReviewIndex ? 'bg-emerald-400 w-4' : 'bg-white/20 w-1.5 hover:bg-white/30'
                    }`}
                  />
                ))}
              </div>

              {/* View all reviews button */}
              <a
                href="https://t.me/bratjanpdd"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 flex items-center justify-center space-x-1.5 text-sm text-emerald-400 hover:text-emerald-300 transition-colors cursor-pointer"
              >
                <i className="ri-telegram-line text-base"></i>
                <span>Посмотреть все отзывы</span>
                <i className="ri-arrow-right-s-line text-base animate-bounce-right"></i>
              </a>
            </div>
          </div>
          
          {/* Micro trigger under reviews */}
          <div className="mt-2 flex items-center justify-center space-x-1.5">
            <i className="ri-shield-check-line text-green-400 text-sm"></i>
            <p className="text-xs font-medium silver-shimmer">
              Сертификат проверяется в базе АвтоЦОна
            </p>
          </div>
        </div>

        {/* How to Get License Section */}
        <div id="how-to-get-section" className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#0360cd]/20 flex items-center justify-center">
              <i className="ri-roadster-line text-[#4d9fff] text-lg"></i>
            </div>
            <h2 className="text-base font-semibold text-white">Получить права очень легко</h2>
          </div>

          <div className="space-y-2.5">
            {/* Step 1 */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-[#0360cd] flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">1</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white mb-0.5">Выбираете категорию</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">Получаете доступ к тестам, видео разборам и урокам ПДД</p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">2</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white mb-0.5">Готовитесь к экзамену</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">Проходите тесты и смотрите уроки. Сертификат готовится 45-75 дней</p>
                </div>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">3</span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white mb-0.5">Получаете сертификат</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">Мы вносим ваш сертификат сразу в базу АвтоЦОна</p>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#0360cd] to-green-500 flex items-center justify-center flex-shrink-0">
                  <i className="ri-trophy-line text-white text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-white mb-0.5">Готово!</h3>
                  <p className="text-sm text-gray-400 leading-relaxed">Сдаете экзамен в АвтоЦоне и получаете права</p>
                </div>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-3 bg-green-500/10 rounded-xl p-3 border border-green-500/20">
            <div className="text-center">
              <p className="text-sm text-white font-medium mb-1.5 leading-relaxed">
                Начните сегодня и получите водительские права нужной категории через 45-75 дней!
              </p>
              <button
                onClick={handleScrollToCategory}
                className="text-sm text-green-400 hover:text-green-300 transition-colors cursor-pointer font-medium whitespace-nowrap"
              >
                Выбрать категорию →
              </button>
            </div>
          </div>
        </div>

        {/* Certificate Preview */}
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.22s' }}>
          {/* Divider before МВД block */}
          <div className="mb-4">
            <div className="h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
          </div>

          {/* МВД РК Quote Block - Simplified */}
          <div className="mb-4 bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
            <div className="flex items-start space-x-2.5">
              <div className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-government-line text-gray-400 text-sm"></i>
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1.5">
                  <span className="text-xs font-medium text-gray-400">МВД РК</span>
                  <span className="text-xs text-gray-500">•</span>
                  <span className="text-xs text-gray-500">23.04.2024</span>
                </div>
                <p className="text-sm text-gray-300 leading-relaxed">
                  С 23 апреля 2024 года для сдачи экзамена на получение водительских прав необходимо пройти обучение в лицензированной автошколе и получить сертификат об окончании курса.
                </p>
              </div>
            </div>
          </div>
          
          {/* Micro trigger under МВД block */}
          <div className="mb-4 flex items-center justify-center space-x-1.5">
            <i className="ri-shield-star-line text-[#4d9fff] text-sm"></i>
            <p className="text-xs font-medium silver-shimmer">
              Мы — лицензированная автошкола
            </p>
          </div>

          {/* Divider after МВД block */}
          <div className="mb-4">
            <div className="h-px bg-gradient-to-r from-transparent via-white/15 to-transparent"></div>
          </div>

          <div className="flex items-center space-x-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#0360cd]/20 flex items-center justify-center">
              <i className="ri-award-line text-[#4d9fff] text-lg"></i>
            </div>
            <h2 className="text-base font-semibold text-white">Наш сертификат</h2>
          </div>

          <div ref={certificateSectionRef} className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur rounded-2xl p-4 border border-white/10 shadow-xl">
            <div 
              className="relative rounded-xl overflow-hidden shadow-lg cursor-pointer group"
              onClick={() => {
                if (window.ym) {
                  window.ym(106703418, 'reachGoal', 510641637);
                }
                setShowVideoModal(true);
              }}
            >
              {!imageErrors['certificate'] ? (
                <img 
                  src="https://static.readdy.ai/image/0839cda7a092ae1c52cedfe932f75ea1/37ec1a4e70e1f377091bd088e6237be4.jpeg" 
                  alt="Сертификат ПДД" 
                  className="w-full h-52 object-cover object-top transition-transform duration-300 group-hover:scale-105"
                  loading="eager"
                  onError={() => handleImageError('certificate')}
                />
              ) : (
                <div className="w-full h-52 bg-gradient-to-br from-[#0360cd]/20 to-[#0360cd]/10 flex items-center justify-center">
                  <i className="ri-award-line text-[#4d9fff] text-5xl"></i>
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              {/* Play button overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-14 h-14 bg-white/20 backdrop-blur rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-110">
                  <i className="ri-play-fill text-white text-3xl ml-1"></i>
                </div>
              </div>
              <div className="absolute bottom-2 left-2 right-2">
                <div className="flex items-center justify-center bg-white/10 backdrop-blur rounded-lg py-2.5 px-3">
                  <i className="ri-shield-check-line text-green-400 text-lg mr-2 flex-shrink-0"></i>
                  <span className="text-white text-sm font-medium text-center">100% гарантия • Проверяется в базе АвтоЦона • имеет QR-код</span>
                </div>
              </div>
            </div>

            <button
              onClick={() => {
                if (window.ym) {
                  window.ym(106703418, 'reachGoal', 510641637);
                }
                setShowVideoModal(true);
              }}
              className="w-full mt-4 py-3 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap bg-[#0360cd] text-white shadow-lg shadow-[#0360cd]/30 hover:bg-[#0360cd]/90 active:scale-[0.98] text-sm"
            >
              <i className="ri-award-line text-lg"></i>
              <span>Получить сертификат</span>
            </button>
          </div>
        </div>

        {/* Why Us Section */}
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.25s' }}>
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#0360cd]/20 flex items-center justify-center">
              <i className="ri-question-line text-[#4d9fff] text-lg"></i>
            </div>
            <h2 className="text-base font-semibold text-white">Почему выбирают нас</h2>
          </div>

          <div className="grid grid-cols-2 gap-2.5">
            <div className="bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
              <div className="w-9 h-9 rounded-lg bg-green-500/20 flex items-center justify-center mb-2">
                <i className="ri-award-line text-green-400 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-white mb-0.5">Сертификат</h3>
              <p className="text-sm text-gray-400">Без посредников и перекупов</p>
            </div>

            <div className="bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
              <div className="w-9 h-9 rounded-lg bg-[#0360cd]/20 flex items-center justify-center mb-2">
                <i className="ri-verified-badge-line text-[#4d9fff] text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-white mb-0.5">Официально</h3>
              <p className="text-sm text-gray-400">Сертификат в базе АвтоЦона</p>
            </div>

            <div className="bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
              <div className="w-9 h-9 rounded-lg bg-orange-500/20 flex items-center justify-center mb-2">
                <i className="ri-smartphone-line text-orange-400 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-white mb-0.5">Удобно</h3>
              <p className="text-sm text-gray-400">Учись с телефона в любое время</p>
            </div>

            <div className="bg-white/5 backdrop-blur rounded-xl p-3.5 border border-white/10">
              <div className="w-9 h-9 rounded-lg bg-purple-500/20 flex items-center justify-center mb-2">
                <i className="ri-customer-service-2-line text-purple-400 text-lg"></i>
              </div>
              <h3 className="text-sm font-semibold text-white mb-0.5">Поддержка</h3>
              <p className="text-sm text-gray-400">Личный куратор 24/7</p>
            </div>
          </div>

          <div className="mt-3 bg-gradient-to-r from-green-500/10 to-emerald-500/10 rounded-xl p-3.5 border border-green-500/20">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
                <i className="ri-shield-check-line text-green-400 text-lg"></i>
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-white">100% гарантия</h3>
                <p className="text-sm text-gray-400">Выдадим сертификат или полностью вернём ваши деньги</p>
              </div>
            </div>
          </div>
        </div>

        {/* Divider before Categories */}
        <div className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.26s' }}>
          <div className="flex items-center justify-center space-x-3">
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#0360cd]/30 to-transparent"></div>
            <div className="flex items-center space-x-2">
              <i className="ri-arrow-down-line text-[#4d9fff]/60 text-sm animate-bounce"></i>
              <span className="text-xs text-gray-500 font-medium">Выберите категорию</span>
              <i className="ri-arrow-down-line text-[#4d9fff]/60 text-sm animate-bounce"></i>
            </div>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#0360cd]/30 to-transparent"></div>
          </div>
        </div>

        {/* Categories - SALES SECTION - LARGER TEXT */}
        <div id="category-section" ref={categorySectionRef} className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.28s' }}>
          <div className="bg-gradient-to-br from-[#0360cd]/15 via-[#0360cd]/10 to-transparent backdrop-blur-xl rounded-2xl p-4 border-2 border-[#0360cd]/30 shadow-lg shadow-[#0360cd]/10">
            <div className="flex items-center justify-center space-x-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#0360cd] flex items-center justify-center shadow-lg shadow-[#0360cd]/30">
                <i className="ri-steering-2-line text-white text-xl"></i>
              </div>
              <h2 className="text-lg font-bold text-white">Выбери свою категорию</h2>
            </div>
            
            <p className="text-sm text-gray-400 text-center mb-4">Обучение и оформление сертификата начинается сразу</p>
            
            <div className="space-y-3">
              {categories.filter(c => !c.disabled).map((category) => {
                const isSelected = selectedCategory === category.id;
                return (
                  <div key={category.id}>
                    <div
                      className={`relative bg-white/5 backdrop-blur rounded-xl p-4 cursor-pointer transition-all duration-200 border ${
                        isSelected 
                          ? 'ring-2 ring-[#0360cd] bg-[#0360cd]/10 border-[#0360cd]/30' 
                          : 'border-white/10 hover:bg-white/10'
                      }`}
                      onClick={() => handleCategorySelect(category.id)}
                    >
                      {category.popular && (
                        <span className="absolute -top-2 right-3 bg-emerald-500 text-white px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                          Популярная
                        </span>
                      )}

                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-white text-base">{category.name}</h3>
                          <p className="text-sm text-gray-400">{category.description}</p>
                        </div>

                        <div className="text-right flex-shrink-0 ml-3">
                          <div className="text-xs text-gray-500 line-through">{category.oldPrice} ₸</div>
                          <div className="text-lg font-bold text-[#4d9fff]">{category.newPrice} ₸</div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* C1 — нет мест */}
              {categories.filter(c => c.disabled).map((category) => (
                <div key={category.id}>
                  <div
                    className="relative bg-white/[0.03] backdrop-blur rounded-xl p-4 cursor-pointer transition-all duration-200 border border-white/[0.06] opacity-50"
                    onClick={() => setShowNoSpotsModal(true)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-500 text-base">{category.name}</h3>
                        <p className="text-sm text-gray-600">{category.description}</p>
                      </div>

                      <div className="text-right flex-shrink-0 ml-3">
                        <div className="text-sm font-semibold text-red-400/70">Нет мест</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Choice Screen - Развилка */}
        {selectedCategory && showChoiceScreen && (
          <div id="choice-screen" className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            <div className="relative bg-gradient-to-br from-[#0360cd]/15 via-[#0360cd]/10 to-transparent backdrop-blur-xl rounded-2xl p-4 border-2 border-[#0360cd]/30 shadow-lg shadow-[#0360cd]/10 overflow-hidden">
              {/* Decorative elements */}
              <div className="absolute -top-8 -right-8 w-24 h-24 bg-[#0360cd]/20 rounded-full blur-3xl pointer-events-none"></div>
              
              {/* Заголовок */}
              <div className="relative text-center mb-3.5">
                <h2 className="text-base font-bold text-white mb-1">
                  Вы можете начать обучение сейчас или задать вопросы менеджеру
                </h2>
              </div>

              {/* Две карточки */}
              <div className="relative space-y-2.5">
                {/* Карточка 1 - Оплата */}
                <div 
                  className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur rounded-xl p-3.5 cursor-pointer transition-all duration-200 border border-white/20 hover:border-[#0360cd]/50 hover:from-[#0360cd]/10 hover:to-[#0360cd]/5 transition-all shadow-lg"
                  onClick={handleContinueToPayment}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-[#0360cd] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#0360cd]/30 group-hover:scale-105 transition-transform">
                      <i className="ri-bank-card-line text-white text-xl"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-white mb-0.5">
                        Оплатить сейчас
                      </h3>
                      <p className="text-[13px] text-gray-400 leading-relaxed">
                        И сразу начать обучение
                      </p>
                    </div>
                    <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#0360cd] transition-colors">
                      <i className="ri-arrow-right-line text-white text-base"></i>
                    </div>
                  </div>
                </div>

                {/* Разделитель */}
                <div className="flex items-center justify-center py-0.5">
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                  <span className="text-xs text-gray-500 font-medium px-3">или</span>
                  <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
                </div>

                {/* Карточка 2 - WhatsApp */}
                <div 
                  className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur rounded-xl p-3.5 cursor-pointer transition-all duration-200 border border-white/20 hover:border-green-500/50 hover:from-green-500/10 hover:to-green-500/5 transition-all shadow-lg"
                  onClick={handleWhatsAppFromChoice}
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500 flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/30 group-hover:scale-105 transition-transform">
                      <i className="ri-whatsapp-line text-white text-xl"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-white mb-0.5">
                        Помочь разобраться
                      </h3>
                      <p className="text-[13px] text-gray-400 leading-relaxed">
                        Задать вопросы в WhatsApp
                      </p>
                    </div>
                    <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-green-500 transition-colors">
                      <i className="ri-arrow-right-line text-white text-base"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Подпись */}
              <div className="mt-3 text-center">
                <p className="text-xs silver-shimmer font-medium">
                  <i className="ri-shield-check-line text-green-400/60 text-xs"></i> Без звонков и спама
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Payment Options - SALES SECTION */}
        {selectedCategory && !showChoiceScreen && (
          <div id="payment-section" className="mb-6 animate-fade-in-up" style={{ animationDelay: '0.35s' }}>
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-[#0360cd]/20 flex items-center justify-center">
                <i className="ri-bank-card-line text-[#4d9fff] text-lg"></i>
              </div>
              <h2 className="text-base font-semibold text-white">Завершите оформление</h2>
            </div>

            <div className="grid grid-cols-2 gap-2.5 mb-4">
              {paymentOptions.map((option) => {
                const isSelected = paymentOption === option.id;
                return (
                  <div
                    key={option.id}
                    className={`relative bg-white/5 backdrop-blur rounded-xl p-3.5 cursor-pointer transition-all duration-200 border ${
                      isSelected 
                        ? 'ring-2 ring-[#0360cd] bg-[#0360cd]/10 border-[#0360cd]/30' 
                        : 'border-white/10 hover:bg-white/10'
                    }`}
                    onClick={() => {
                      // Яндекс.Метрика: отправка цели
                      if (window.ym) {
                        window.ym(106703418, 'reachGoal', 511296686, {
                          payment: option.id
                        });
                      }
                      setPaymentOption(option.id);
                    }}
                  >
                    {option.recommended && (
                      <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#0360cd] text-white px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap">
                        Выгодно
                      </span>
                    )}
                    
                    <div className="text-center pt-1">
                      <h3 className="font-bold text-white text-xl">{option.title}</h3>
                      <p className="text-xs text-gray-400 mt-0.5">{option.description}</p>
                    </div>

                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 bg-[#0360cd] rounded-full flex items-center justify-center">
                        <i className="ri-check-line text-white text-xs"></i>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Payment Details - SALES SECTION */}
            {selectedPaymentData && (
              <div key={paymentOption} className="bg-gradient-to-br from-[#0360cd]/10 to-[#0360cd]/5 backdrop-blur rounded-xl p-4 border border-[#0360cd]/20 animate-fade-in mb-4">
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-base font-semibold text-white">К оплате сейчас:</span>
                    <span className="text-2xl font-bold text-[#4d9fff]">
                      {paymentOption === '50/50' ? `${categoryHalfPrice} ₸` : `${categoryPrice} ₸`}
                    </span>
                  </div>
                  {paymentOption === '50/50' && (
                    <div className="mt-2 bg-white/5 rounded-lg px-3 py-2 border border-white/10">
                      <p className="text-sm text-gray-300 leading-relaxed text-center font-medium">
                        Остаток <span className="text-[#4d9fff]">{categoryHalfPrice} ₸</span> вы можете оплатить<br />
                        при готовности сертификата
                      </p>
                    </div>
                  )}
                </div>

                <div className="bg-white/5 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-400 mb-2 font-medium">Что входит в обучение:</p>
                  <div className="space-y-1.5">
                    <div className="flex items-start space-x-2">
                      <i className="ri-book-open-line text-[#4d9fff] text-sm mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-300">1500 актуальных вопросов АвтоЦона</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <i className="ri-video-line text-[#4d9fff] text-sm mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-300">Видеоуроки по всем темам ПДД</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <i className="ri-time-line text-[#4d9fff] text-sm mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-300">
                        {paymentOption === '50/50' ? 'Доступ к тестам на 3 месяца' : 'Доступ к тестам на 1 год'}
                      </span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <i className="ri-award-line text-[#4d9fff] text-sm mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-300">Сертификат категории {selectedCategory}</span>
                    </div>
                    <div className="flex items-start space-x-2">
                      <i className="ri-customer-service-2-line text-[#4d9fff] text-sm mt-0.5 flex-shrink-0"></i>
                      <span className="text-sm text-gray-300">Личный куратор 24/7</span>
                    </div>
                  </div>
                </div>

                <div className="mb-4">
                  <div className="flex items-center justify-center space-x-2 bg-green-500/10 rounded-lg py-2 px-3 border border-green-500/30">
                    <i className="ri-shield-check-line text-green-400 text-lg animate-pulse"></i>
                    <p className="text-sm text-green-400 font-medium">
                      Гарантия выдачи сертификата или полный возврат денег
                    </p>
                  </div>
                </div>

                {/* Payment Error Message */}
                {paymentError && (
                  <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3">
                    <div className="flex items-start space-x-2">
                      <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0"></i>
                      <span className="text-sm text-red-400">{paymentError}</span>
                    </div>
                    {paymentLogs && (
                      <button
                        onClick={handleDownloadLogs}
                        className="mt-2 w-full py-2.5 rounded-lg bg-amber-500/20 border border-amber-500/30 text-amber-400 text-sm font-medium flex items-center justify-center space-x-2 cursor-pointer hover:bg-amber-500/30 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-download-line text-base"></i>
                        <span>Скачать логи для интегратора</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Cert Name Error */}
                {certNameError && (
                  <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center space-x-2 animate-shake">
                    <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0"></i>
                    <span className="text-sm text-red-400">Введите имя и фамилию</span>
                  </div>
                )}

                {/* Cert IIN Error */}
                {certIinError && (
                  <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center space-x-2 animate-shake">
                    <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0"></i>
                    <span className="text-sm text-red-400">ИИН должен содержать 12 цифр</span>
                  </div>
                )}

                {/* Error Message */}
                {showError && (
                  <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center space-x-2 animate-shake">
                    <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0"></i>
                    <span className="text-sm text-red-400">Пожалуйста, выберите категорию</span>
                  </div>
                )}

                {/* Phone Error Message */}
                {phoneError && (
                  <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-xl p-3 flex items-center space-x-2 animate-shake">
                    <i className="ri-error-warning-line text-red-400 text-base flex-shrink-0"></i>
                    <span className="text-sm text-red-400">Пожалуйста, введите номер WhatsApp</span>
                  </div>
                )}

                {/* Имя и Фамилия */}
                <label className="block text-sm text-gray-300 mb-2 font-medium">
                  <i className="ri-user-line text-[#4d9fff] mr-1.5"></i>
                  Имя и фамилия
                </label>
                <div className="grid grid-cols-2 gap-2.5 mb-4">
                  <div className={`relative rounded-xl border ${certNameError && !certFirstName.trim() ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/5'} focus-within:border-[#0360cd]/50 transition-all`}>
                    <input
                      type="text"
                      value={certFirstName}
                      onChange={e => { setCertFirstName(e.target.value); setCertNameError(false); }}
                      placeholder="Имя"
                      className="w-full bg-transparent text-white placeholder-gray-500 px-3.5 py-3.5 rounded-xl outline-none text-sm"
                    />
                  </div>
                  <div className={`relative rounded-xl border ${certNameError && !certLastName.trim() ? 'border-red-500/50 bg-red-500/5' : 'border-white/10 bg-white/5'} focus-within:border-[#0360cd]/50 transition-all`}>
                    <input
                      type="text"
                      value={certLastName}
                      onChange={e => { setCertLastName(e.target.value); setCertNameError(false); }}
                      placeholder="Фамилия"
                      className="w-full bg-transparent text-white placeholder-gray-500 px-3.5 py-3.5 rounded-xl outline-none text-sm"
                    />
                  </div>
                </div>

                {/* ИИН */}
                <label className="block text-sm text-gray-300 mb-2 font-medium">
                  <i className="ri-id-card-line text-[#4d9fff] mr-1.5"></i>
                  ИИН
                </label>
                <div className={`relative bg-white/5 rounded-xl border mb-1 ${certIinError ? 'border-red-500/50' : 'border-white/10'} focus-within:border-[#0360cd]/50 transition-colors`}>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={certIin}
                    onChange={e => {
                      const val = e.target.value.replace(/\D/g, '').slice(0, 12);
                      setCertIin(val);
                      setCertIinError(false);
                    }}
                    placeholder="123456789012"
                    maxLength={12}
                    className="w-full bg-transparent text-white placeholder-gray-500 pl-4 pr-14 py-3.5 rounded-xl outline-none text-sm tracking-widest"
                  />
                  <span className="absolute right-3.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-600 font-mono">
                    {certIin.length}/12
                  </span>
                </div>
                <p className="text-xs text-gray-500 mb-4">Индивидуальный идентификационный номер</p>

                {/* Телефон */}
                <label className="block text-sm text-gray-300 mb-2 font-medium">
                  <i className="ri-whatsapp-line text-green-400 mr-1.5"></i>
                  Номер WhatsApp для связи
                </label>
                <div className={`relative bg-white/5 rounded-xl border ${phoneError ? 'border-red-500/50' : 'border-white/10'} focus-within:border-[#0360cd]/50 transition-colors`}>
                  <input
                    type="tel"
                    value={whatsappNumber}
                    onChange={handlePhoneChange}
                    placeholder="+7 (___) ___-__-__"
                    className="w-full bg-transparent text-white placeholder-gray-500 px-4 py-3.5 rounded-xl outline-none text-base"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  Мы напишем вам сами после оплаты
                </p>
              </div>
            )}

            <button
              onClick={handlePaymentClick}
              className="w-full py-3.5 rounded-xl font-semibold transition-all flex items-center justify-center space-x-2 cursor-pointer whitespace-nowrap bg-[#0360cd] text-white shadow-lg shadow-[#0360cd]/30 hover:bg-[#0360cd]/90 active:scale-[0.98] text-base"
            >
              <span>Оплатить {paymentOption === '50/50' ? categoryHalfPrice : categoryPrice} ₸</span>
            </button>

            {/* Micro triggers */}
            <div className="flex items-center justify-center space-x-2 mt-3 text-xs">
              <div className="flex items-center space-x-1">
                <i className="ri-shield-check-line text-green-400"></i>
                <span className="silver-shimmer font-medium">Безопасная оплата</span>
              </div>
              <span className="text-gray-600">•</span>
              <div className="flex items-center space-x-1">
                <i className="ri-file-list-line text-[#4d9fff]"></i>
                <span className="silver-shimmer font-medium">Чек</span>
              </div>
              <span className="text-gray-600">•</span>
              <div className="flex items-center space-x-1">
                <i className="ri-customer-service-2-line text-orange-400"></i>
                <span className="silver-shimmer font-medium">Поддержка 24/7</span>
              </div>
            </div>

            <div className="text-center text-xs text-gray-500 mt-3">
              <span>Нажимая на кнопку вы принимаете условия </span>
              <a 
                href="https://drive.google.com/file/d/1PgaC4Cx67zz1lB9pSwE2Av62pHb5_N8q/view?usp=sharing" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#4d9fff] hover:text-[#0360cd] underline cursor-pointer transition-colors"
              >
                публичной оферты
              </a>
              <span> и </span>
              <a 
                href="https://drive.google.com/file/d/1PgaC4Cx67zz1lB9pSwE2Av62pHb5_N8q/view?usp=sharing" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-[#4d9fff] hover:text-[#0360cd] underline cursor-pointer transition-colors"
              >
                политики конфиденциальности
              </a>
            </div>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-8 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
          <div className="flex items-center space-x-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#0360cd]/20 flex items-center justify-center">
              <i className="ri-question-answer-line text-[#4d9fff] text-lg"></i>
            </div>
            <h2 className="text-base font-semibold text-white">Частые вопросы</h2>
          </div>

          <div className="space-y-2.5">
            <FAQItem 
              question="Можно ли учиться онлайн?" 
              answer="Да, можно. Но мы рекомендуем дополнительно взять уроки у инструкторов по вождению в вашем городе, чтобы лучше подготовиться к экзамену по вождению."
            />
            <FAQItem 
              question="Сертификат официальный?" 
              answer="Да, сертификат полностью официальный и вносится в базу АвтоЦОна. Вы можете проверить его онлайн по QR-коду или номеру сертификата."
            />
            <FAQItem 
              question="Сколько времени занимает получение?" 
              answer="В зависимости от категории: A1 — 45 дней, A — 60 дней, B — 75 дней. Это время необходимо для прохождения обучения и оформления документов."
            />
            <FAQItem 
              question="Нужно ли посещать автошколу?" 
              answer="Нет, всё обучение проходит онлайн. Вы учитесь в удобное время с телефона или компьютера."
            />
            <FAQItem 
              question="Как проходит оплата?" 
              answer="Вы можете оплатить сразу 100% или по схеме 50/50 — половину сейчас, остаток при готовности сертификата. Принимаем Kaspi, карты и переводы."
            />
            <FAQItem 
              question="Что если я не сдам экзамен?" 
              answer="Мы предоставляем полную подготовку к экзамену. Если вы не сдадите с первого раза, поможем подготовиться к пересдаче бесплатно."
            />
            <FAQItem 
              question="Есть ли гарантия?" 
              answer="Да, 100% гарантия выдачи сертификата. Если по какой-то причине мы не сможем выдать сертификат — вернём деньги полностью."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 pt-5 border-t border-white/10 text-center animate-fade-in" style={{ animationDelay: '0.55s' }}>
          {/* Documents Link */}
          <div className="mb-3">
            <a
              href="https://drive.google.com/file/d/1PgaC4Cx67zz1lB9pSwE2Av62pHb5_N8q/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-[#4d9fff] underline cursor-pointer transition-colors"
            >
              Публичная оферта и политика конфиденциальности
            </a>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col items-center space-y-2 mb-3">
            <a
              href="https://wa.me/77066319969"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center space-x-1.5 text-xs text-gray-500 hover:text-[#4d9fff] transition-colors cursor-pointer"
            >
              <i className="ri-whatsapp-line text-sm"></i>
              <span>+7 706 631 99 69</span>
            </a>
            <a
              href="mailto:support@bratjan.com"
              className="flex items-center space-x-1.5 text-xs text-gray-500 hover:text-[#4d9fff] transition-colors cursor-pointer"
            >
              <i className="ri-mail-line text-sm"></i>
              <span>support@bratjan.com</span>
            </a>
          </div>

          <div className="flex justify-center">
            <span className="text-gray-600 text-xs">© 2026 Bratjan PDD</span>
          </div>

          {/* Legal Info */}
          <div className="mt-2 flex flex-col items-center space-y-0.5">
            <span className="text-gray-600 text-xs">ИП EKI DONGELEK</span>
            <span className="text-gray-600 text-xs">БИН 010609600408</span>
          </div>
        </div>
      </div>
    </div>
  );
}
